/* Phase 3 (original roadmap) · frontend module gating. Pure UX layer — the backend authorization (channel_authz) is authoritative.
   Oct 2026: the UI follows the Super-Admin module selection for every MANAGED cafe in every mode (off/shadow/on); backend API denial
   still applies only when the cafe's effective enforcement mode is ON. Unmanaged cafes render everything as before. */

export const MODULE_LABEL = { erp: "Stock / ERP", pos: "POS Billing", website: "Website Online Ordering", qr: "QR Ordering", daily_report: "Daily Report" };
const PRINT = ["pos", "website", "qr"]; // printers / templates are shared by every printing channel
const MENU = ["pos", "website", "qr"];  // one menu serves POS, Website and QR

// First matching prefix wins (longer prefixes first). `any`: user needs at least one of the modules.
const RULES = [
  ["/manager/pos/qr-banners", ["qr"]],
  ["/manager/pos/tables", ["pos", "qr"], "pos"],
  ["/manager/pos/printers", PRINT], ["/manager/print-templates", PRINT], ["/manager/pos-print-designer", PRINT], ["/manager/pos-restaurant-details", PRINT],
  ["/manager/pos/items", MENU], ["/manager/pos/categories", MENU], ["/manager/pos/variants", MENU], ["/manager/pos/addons", MENU], ["/manager/pos/combos", MENU],
  ["/manager/pos/discounts", MENU], ["/manager/pos/bulk-menu", MENU],
  ["/manager/pos/orders", ["pos"]], ["/manager/pos/reports", ["pos"]], ["/manager/pos/dashboard", ["pos"]],
  ["/manager/pos", ["pos", "qr"], "pos"], // QR inbox lives inside POS Billing → QR-only branches keep the page (banner when POS itself is missing)
  ["/manager/dashboard", ["erp"]], ["/manager/team", ["erp"]], ["/manager/captain-performance", ["erp"]], // Stock dashboard + captain (stock staff) management
  ["/manager/quick-bill", ["pos"]], ["/manager/order-history", ["pos"]], ["/manager/pos-performance", ["pos"]],
  ["/manager/website-orders", ["website"]], ["/online-ordering", ["website"]], ["/online-payments", ["website"]],
  ["/manager/daily-report", ["daily_report"]], ["/owner/manager-reports", ["daily_report"]], ["/owner/daily-reports", ["daily_report"]],
  ["/owner/branches/", ["daily_report"]], // …/:branchId/daily-report-settings
  ["/inventory", ["erp"]], ["/available-stock", ["erp"]], ["/manager/transfers", ["erp"]], ["/manager/items", ["erp"]], ["/manager/categories", ["erp"]],
  ["/vendors", ["erp"]], ["/purchase-orders", ["erp"]], ["/manager/deliveries", ["erp"]], ["/deliveries", ["erp"]], ["/manager/wastage", ["erp"]],
  ["/manager/opening-orders", ["erp"]], ["/opening-orders", ["erp"]], ["/manager/direct-sales", ["erp"]], ["/manager/sales-history", ["erp"]],
  ["/stock-complaints", ["erp"]], ["/manager/reports", ["erp"]], ["/reports", ["erp"]], ["/manager/bulk", ["erp"]], ["/approvals", ["erp"]],
  ["/captain/inventory", ["erp"]], ["/captain/transfers", ["erp"]], ["/captain/wastage", ["erp"]], ["/captain/my-orders", ["erp"]], ["/captain/my-history", ["erp"]], ["/captain/closing", ["erp"]],
];

function rule(pathname) {
  const p = pathname || "";
  return RULES.find(([prefix]) => p === prefix || p.startsWith(prefix.endsWith("/") ? prefix : prefix + "/")) || null;
}

export function requiredModules(pathname) {
  const hit = rule(pathname);
  return hit ? hit[1] : null;
}

/* Module whose absence deserves an explanatory banner on a partially-available page (null → no banner). */
export function bannerModule(pathname) {
  const hit = rule(pathname);
  return hit ? hit[2] || null : null;
}

export function restrictionsActive(auth) {
  const e = auth?.entitlements;
  return !!(e?.restrictions_active || e?.managed);
}

function branchModules(auth, branchId) {
  return auth?.effectiveModules?.[branchId]?.modules || null;
}

/* Which of `modules` the user has. Staff → own branch; owner/super admin → union over all branches (branch-specific actions pass branchId). */
export function grantedOf(auth, modules, branchId) {
  if (!restrictionsActive(auth)) return modules;
  const user = auth.user || {};
  if (user.role === "super_admin") return modules;
  const bid = branchId || user.branch_id;
  if (bid) {
    const m = branchModules(auth, bid);
    return m ? modules.filter((k) => m[k]) : [];
  }
  const all = Object.values(auth.effectiveModules || {});
  return modules.filter((k) => all.some((b) => b.modules?.[k]));
}

export function isAllowed(auth, modules, branchId) {
  if (!modules || !modules.length) return true;
  return grantedOf(auth, modules, branchId).length > 0;
}

export function pathAllowed(auth, pathname) {
  return isAllowed(auth, requiredModules(pathname));
}
